import { fmtCoilKg } from "@/lib/utils";
import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Truck, ScanLine, Camera, Loader2, X, Upload, CheckCircle2, XCircle, AlertTriangle,
  FileText, Trash2, Flag, ArrowLeft, Sparkles, Package, History, Eye, Download,
} from "lucide-react";
import {
  listSalesOrders, getSalesOrder, executeSODispatch,
  extractEInvoice, extractKantaParchi, getDispatchStock, checkCoil,
  splitFGPallet, fgLabelsPdfUrl, fgLabelsTsplUrl,
  listSOScans, addSOScan, removeSOScan,
  listSODispatches, approveDispatchMismatch, rejectDispatchMismatch,
  soGatePassPdfUrl, dispatchGatePassPdfUrl, openPdfInWindow,
  listDispatchHistory, listDispatchHistoryCustomers, dispatchHistoryExportUrl,
  getDispatchDetails, dispatchEInvoiceUrl, dispatchKantaParchiUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import PrintButton from "@/components/PrintButton";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Scissors } from "lucide-react";

const STATUS_STYLE = {
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
};

// Loosely compare two customer names: strips case, punctuation, invisible
// whitespace (NBSP, zero-width joiners, tabs) and common corporate suffixes
// (Pvt, Private, Ltd, Limited, LLP, Inc, Co, Corp) so
// "GSP Electronics P Ltd" == "GSP Electronics Pvt Ltd" == "GSP Electronics Private Limited".
const normalizeCustomer = (s) => (s || "")
  // Kill NBSP, thin-space, zero-width joiners, BOM, tabs, newlines (Excel / PDF copy-paste hazards)
  .replace(/[\u00A0\u2000-\u200D\u202F\u205F\u3000\uFEFF\t\r\n]/g, " ")
  .toLowerCase()
  .replace(/[.,&/'"`-]/g, " ")
  .replace(/\bprivate\b/g, "pvt")
  .replace(/\blimited\b/g, "ltd")
  .replace(/\b(pvt|ltd|llp|inc|co|corp|corporation|company)\b/g, " ")
  .replace(/\bp\b/g, " ")            // stand-alone "p" (as in "P Ltd") — safe to drop
  .replace(/\s+/g, " ")
  .trim();
const sameCustomer = (a, b) => normalizeCustomer(a) === normalizeCustomer(b);

// FastAPI 422 responses ship an ARRAY of Pydantic error objects. Rendering
// that array (via toast.error() or elsewhere in JSX) throws
// "Objects are not valid as a React child…". Always coerce to a friendly
// string before we hand it to the UI.
const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || fallback;
};

const openGatePass = (url) => {
  const win = window.open("", "_blank");
  openPdfInWindow(url, win).catch((e) => toast.error(e.message));
};

const KindPill = ({ kind }) => (
  <span
    className={`inline-flex items-center text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${
      kind === "fg" ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    : "bg-blue-100 text-blue-800 border border-blue-200"
    }`}
  >
    {kind === "fg" ? "FG" : "SFG"}
  </span>
);

// -------------------------------------------------------------------
// Step 1 — pick a pending order (tile grid)
// -------------------------------------------------------------------
const PendingTiles = ({ onPick }) => {
  const { user, hasModule } = useAuth();
  const isApprover = user?.role === "director" || hasModule("dispatch.approve");
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders", "pending-tiles"],
    queryFn: async () => {
      const [c, p] = await Promise.all([
        listSalesOrders({ status: "confirmed" }),
        listSalesOrders({ status: "in_progress" }),
      ]);
      return [...(c || []), ...(p || [])];
    },
    refetchInterval: 8000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Step 1</p>
          <h2 className="font-heading text-lg font-bold text-slate-900">Pick an order to dispatch</h2>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      )}
      {!isLoading && orders.length === 0 && (
        <p className="text-center py-12 text-sm text-slate-500 italic">No open orders — create one on the Orders page first.</p>
      )}
      {!isLoading && orders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dispatch-pending-tiles">
          {orders.map((o) => {
            const fgLines = (o.lines || []).filter((l) => (l.kind || "sfg") === "fg" && (l.ordered_kg - (l.delivered_kg || 0)) > 0);
            const sfgLines = (o.lines || []).filter((l) => (l.kind || "sfg") === "sfg" && (l.ordered_kg - (l.delivered_kg || 0)) > 0);
            return (
              <button
                key={o.id}
                data-testid={`dispatch-tile-${o.order_number}`}
                onClick={() => onPick(o.id)}
                className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-900 hover:shadow-md transition-all flex flex-col gap-2 min-h-[200px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-xs tracking-widest text-slate-500 font-semibold">{o.order_number}</p>
                    <p className="font-heading text-lg font-bold text-slate-900 truncate mt-0.5">{o.customer_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {o.priority === "urgent" && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
                        <Flag className="h-3 w-3" /> Urgent
                      </span>
                    )}
                    <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${STATUS_STYLE[o.status] || ""}`}>{o.status.replace("_", " ")}</span>
                    {o.has_pending_approval && (
                      isApprover ? (
                        <span
                          data-testid={`approval-badge-${o.order_number}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white"
                        >
                          <AlertTriangle className="h-3 w-3" /> Management approval required
                        </span>
                      ) : (
                        <span
                          data-testid={`approval-badge-${o.order_number}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200"
                        >
                          <Loader2 className="h-3 w-3" /> Pending approval
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div className="flex-1 space-y-1">
                  {fgLines.length > 0 && (
                    <div className="rounded-sm bg-emerald-50/60 border border-emerald-100 px-2 py-1">
                      <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-emerald-800 mb-0.5">FG</p>
                      {fgLines.slice(0, 3).map((l) => (
                        <div key={l.line_id || l.sku_name} className="flex items-center justify-between text-xs">
                          <span className="font-mono font-semibold text-slate-900 truncate">{l.sku_name}</span>
                          <span className="font-mono text-slate-600 shrink-0 ml-2">{l.ordered_kg - (l.delivered_kg || 0)} kg</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {sfgLines.length > 0 && (
                    <div className="rounded-sm bg-blue-50/60 border border-blue-100 px-2 py-1">
                      <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-blue-800 mb-0.5">SFG</p>
                      {sfgLines.slice(0, 3).map((l) => (
                        <div key={l.line_id || l.sku_name} className="flex items-center justify-between text-xs">
                          <span className="font-mono font-semibold text-slate-900 truncate">{l.sku_name}</span>
                          <span className="font-mono text-slate-600 shrink-0 ml-2">{l.ordered_kg - (l.delivered_kg || 0)} kg</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-2 text-[11px] text-slate-500 flex justify-between items-center gap-2">
                  <span>Target <span className="font-mono text-slate-800">{o.target_delivery_date || "—"}</span></span>
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`gate-pass-download-${o.order_number}`}
                    onClick={(e) => { e.stopPropagation(); openGatePass(soGatePassPdfUrl(o.id)); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); openGatePass(soGatePassPdfUrl(o.id)); } }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white text-slate-700 font-semibold text-[10px] tracking-wider uppercase cursor-pointer transition-colors"
                    title="Open the Gate Pass PDF for every dispatch on this SO"
                  >
                    <FileText className="h-3 w-3" /> Gate Pass
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// Line row inside Step 2 — shows requested vs. scanned vs. invoice vs. live stock
// -------------------------------------------------------------------
const RequestedLineRow = ({ line, scannedKg, invoiceKg }) => {
  const kind = line.kind || "sfg";
  const requested = Math.max(0, (line.ordered_kg || 0) - (line.delivered_kg || 0));
  const { data: stock } = useQuery({
    queryKey: ["dispatch-stock", kind, line.sku_name],
    queryFn: () => getDispatchStock(line.sku_name, kind),
    refetchInterval: 15000,
  });

  const diff = invoiceKg == null ? null : Math.round((scannedKg - invoiceKg) * 100) / 100;
  const state = invoiceKg == null
    ? "waiting"
    : Math.abs(diff) <= 2
      ? (Math.abs(diff) < 0.01 ? "exact" : (diff > 0 ? "over" : "under"))
      : "mismatch";

  const badgeStyle = {
    waiting: "bg-slate-100 text-slate-600 border-slate-200",
    exact: "bg-green-100 text-green-800 border-green-200",
    over: "bg-amber-100 text-amber-800 border-amber-200",
    under: "bg-amber-100 text-amber-800 border-amber-200",
    mismatch: "bg-red-100 text-red-800 border-red-200",
  }[state];

  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`req-line-${kind}-${line.sku_name}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <KindPill kind={kind} />
          <span className="font-mono font-bold text-slate-900 truncate">{line.sku_name}</span>
        </div>
        <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm border ${badgeStyle}`}>
          {state === "waiting" && "Awaiting invoice"}
          {state === "exact" && "Exact ✓"}
          {state === "over" && `Over (+${diff.toFixed(1)})`}
          {state === "under" && `Under (${diff.toFixed(1)})`}
          {state === "mismatch" && `Mismatch (${diff > 0 ? "+" : ""}${diff.toFixed(1)} kg)`}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
        <div>
          <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Ordered</p>
          <p className="font-mono font-bold text-slate-900">{requested} kg</p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">In stock</p>
          <p className="font-mono font-bold text-slate-800">{stock ? `${stock.total_kg} kg` : "…"}<span className="text-slate-500 font-normal"> {stock ? `(${stock.count} pcs)` : ""}</span></p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Scanned</p>
          <p className={`font-mono font-bold ${scannedKg > 0 ? "text-blue-700" : "text-slate-400"}`}>{scannedKg} kg</p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">e-Invoice</p>
          <p className="font-mono font-bold text-slate-800">{invoiceKg == null ? "—" : `${invoiceKg} kg`}</p>
        </div>
      </div>
      {/* Fill bar */}
      <ProgressFillBar ordered={requested} scanned={scannedKg} tolerance={2} />
    </div>
  );
};

// Reusable fill bar — blue while filling, green when within tolerance,
// yellow when over/under beyond tolerance.
const ProgressFillBar = ({ ordered, scanned, tolerance = 100 }) => {
  const balance = ordered - scanned;
  const pctRaw = ordered > 0 ? (scanned / ordered) * 100 : 0;
  const pct = Math.max(0, Math.min(100, pctRaw));
  const overshoot = scanned > ordered + tolerance;
  const underShort = ordered > 0 && scanned < ordered - tolerance && scanned > 0;
  const exact = ordered > 0 && Math.abs(balance) <= tolerance && scanned > 0;
  const color = overshoot || underShort ? "bg-yellow-500" : exact ? "bg-emerald-600" : "bg-blue-600";
  const labelColor = overshoot || underShort ? "text-yellow-800" : exact ? "text-emerald-800" : "text-slate-600";
  return (
    <div className="mt-2" data-testid="dispatch-fill-bar">
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-300`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`flex justify-between text-[10px] font-mono mt-1 ${labelColor}`}>
        <span>{scanned} kg scanned</span>
        {overshoot && <span className="font-semibold">Over by {Math.round((scanned - ordered) * 10) / 10} kg</span>}
        {underShort && <span className="font-semibold">Balance {Math.round(balance * 10) / 10} kg</span>}
        {exact && <span className="font-semibold">Match ✓</span>}
        {!overshoot && !underShort && !exact && <span>Balance {Math.round(balance * 10) / 10} kg</span>}
        <span>{ordered} kg ordered</span>
      </div>
    </div>
  );
};

// Three-way weight-match banner (scanned vs invoice net vs kanta parchi net)
const WeightMatchBanner = ({ scannedKg, invoiceKg, kantaKg, tolerance = 100 }) => {
  const rows = [
    { label: "Scanned", value: scannedKg },
    { label: "Invoice Net", value: invoiceKg },
    { label: "Kanta Parchi Net", value: kantaKg },
  ];
  const filled = rows.filter((r) => r.value != null && r.value > 0);
  const values = filled.map((r) => r.value);
  const anyPairDiff = values.length > 1
    ? Math.max(...values) - Math.min(...values)
    : null;
  const allSet = filled.length === 3;
  const ok = allSet && anyPairDiff <= tolerance;
  const warning = allSet && anyPairDiff > tolerance;
  const stateCls = warning
    ? "bg-yellow-50 border-yellow-300"
    : ok ? "bg-emerald-50 border-emerald-300"
         : "bg-slate-50 border-slate-200";
  const dotCls = warning ? "bg-yellow-500" : ok ? "bg-emerald-600" : "bg-slate-300";
  return (
    <section className={`border rounded-sm p-3 ${stateCls}`} data-testid="weight-match-banner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${dotCls}`} />
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-700">Three-way weight match</p>
        </div>
        <p className={`text-[11px] font-medium ${warning ? "text-yellow-800" : ok ? "text-emerald-800" : "text-slate-600"}`}>
          {warning ? `Off by ${Math.round(anyPairDiff * 10) / 10} kg — tolerance ±${tolerance} kg`
           : ok ? "All within tolerance ✓"
           : `Fill all three (tolerance ±${tolerance} kg)`}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {rows.map((r) => (
          <div key={r.label}>
            <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">{r.label}</p>
            <p className={`font-mono font-bold ${r.value != null && r.value > 0 ? "text-slate-900" : "text-slate-400"}`}>
              {r.value != null && r.value > 0 ? `${r.value} kg` : "—"}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

// -------------------------------------------------------------------
// Step 2 — dispatch execution for the picked SO
// -------------------------------------------------------------------
const DispatchExecute = ({ orderId, onBack }) => {
  const qc = useQueryClient();
  const { user, hasModule } = useAuth();
  const isApprover = user?.role === "director" || hasModule("dispatch.approve");
  const { data: order, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getSalesOrder(orderId),
    enabled: !!orderId,
  });

  // Existing + pending-approval dispatches on this SO (drives the "Awaiting director approval" banner)
  const { data: dispatchesForOrder = [] } = useQuery({
    queryKey: ["order-dispatches", orderId],
    queryFn: () => listSODispatches(orderId),
    enabled: !!orderId,
    refetchInterval: 8000,
  });
  const pendingApprovals = useMemo(
    () => (dispatchesForOrder || []).filter((d) => d.approval_status === "pending"),
    [dispatchesForOrder],
  );
  // Most recent rejected dispatch (if any) — its action_required note tells the
  // operator what to fix before re-dispatching.
  const lastRejected = useMemo(() => {
    const rej = (dispatchesForOrder || []).filter((d) => d.approval_status === "rejected");
    return rej.length ? rej[0] : null;   // list already sorted newest-first server-side
  }, [dispatchesForOrder]);

  const approveMut = useMutation({
    mutationFn: (dispatchId) => approveDispatchMismatch(dispatchId),
    onSuccess: () => {
      toast.success("Dispatch approved · order will close");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["order-dispatches", orderId] });
    },
    onError: (e) => toast.error(errMsg(e, "Approval failed")),
  });

  // Reject flow — reverses the dispatch and captures a required "Action Required" note.
  const [rejecting, setRejecting] = useState(null);        // dispatch doc being rejected
  const [actionNote, setActionNote] = useState("");
  const rejectMut = useMutation({
    mutationFn: ({ id, note }) => rejectDispatchMismatch(id, note),
    onSuccess: (res) => {
      toast.success(`Dispatch reversed · ${res?.reverted_coils ?? 0} coil(s) back in stock`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["order-dispatches", orderId] });
      qc.invalidateQueries({ queryKey: ["dispatch-stock"] });
      setRejecting(null);
      setActionNote("");
    },
    onError: (e) => toast.error(errMsg(e, "Reject failed")),
  });

  // Scanned coils: { barcode, kind, sku_name, weight_kg }
  const [scanned, setScanned] = useState([]);
  const [manualBc, setManualBc] = useState("");
  const [cameraOn, setCameraOn] = useState(false);

  // Partial-FG-dispatch flow state
  // pendingFg: { barcode, sku_name, weight_kg, grade, width_mm, thickness_mm } — awaiting Full/Partial choice
  const [pendingFg, setPendingFg] = useState(null);
  const [partialMode, setPartialMode] = useState(false);      // true → show weight input inside the same dialog
  const [partialInput, setPartialInput] = useState("");
  const [splitPending, setSplitPending] = useState(false);
  // splitResult: { leftover: {...}, dispatched_new: {...} } — shows label print dialog after successful split
  const [splitResult, setSplitResult] = useState(null);

  // e-Invoice extraction state
  const [einvoice, setEinvoice] = useState(null);       // { invoice_number, invoice_date, customer_name, lines, pdf_b64, filename }
  const [extracting, setExtracting] = useState(false);
  const einvoiceInputRef = useRef(null);

  // Weight-bridge slip (Kanta Parchi — MANDATORY, with AI-extracted net weight editable)
  const [slip, setSlip] = useState(null);               // { filename, b64, mime_type, extracted: {...} }
  const [kantaNet, setKantaNet] = useState("");         // Confirmed net weight (worker-editable)
  const [extractingSlip, setExtractingSlip] = useState(false);
  const slipInputRef = useRef(null);
  const manualBcRef = useRef(null);

  // Extra fields
  const [vehicle, setVehicle] = useState("");
  const [driver, setDriver] = useState("");
  const [notes, setNotes] = useState("");
  const [grRr, setGrRr] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");   // controlled string; auto-filled from Gemini, editable by operator
  const [station, setStation] = useState("");

  // Auto-populate vehicle from invoice? No — leave user in control.
  // -----------------------------------------------------------------
  // Load persisted scan reservations from backend on open
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    listSOScans(orderId)
      .then((res) => {
        if (cancelled) return;
        const rows = (res?.scans || []).map((s) => ({
          barcode: s.coil_id, kind: s.kind, sku_name: s.sku_name,
          weight_kg: s.weight_kg, grade: s.grade || "",
          width_mm: s.width_mm, thickness_mm: s.thickness_mm,
        }));
        setScanned(rows);
      })
      .catch(() => {/* silent — new SO or transient error */});
    return () => { cancelled = true; };
  }, [orderId]);

  // -----------------------------------------------------------------
  // Persist a scan reservation (dedupe + cross-SO guard on the backend)
  // -----------------------------------------------------------------
  const persistScan = async (barcode) => {
    const res = await addSOScan(orderId, barcode);
    const s = res.scan;
    setScanned((prev) => [...prev, {
      barcode: s.coil_id, kind: s.kind, sku_name: s.sku_name,
      weight_kg: s.weight_kg, grade: s.grade || "",
      width_mm: s.width_mm, thickness_mm: s.thickness_mm,
    }]);
    if (res.so_status === "in_progress") {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    }
  };

  // -----------------------------------------------------------------
  // Barcode add / dedupe / lookup weight from backend
  // For SFG → persist immediately.
  // For FG  → open the Full/Partial choice dialog first; only after
  //           the operator confirms Full (or after a successful split)
  //           does the coil get persisted.
  // -----------------------------------------------------------------

  const addBarcode = async (raw) => {
    const bc = (raw || "").trim();
    if (!bc) return;
    if (scanned.some((s) => s.barcode.toLowerCase() === bc.toLowerCase())) {
      toast.error(`${bc} already scanned — skipped`);
      return;
    }
    if (pendingFg && pendingFg.barcode.toLowerCase() === bc.toLowerCase()) {
      toast.error(`${bc} awaiting Full/Partial choice`);
      return;
    }
    try {
      const body = await checkCoil(bc);
      if (!body.exists || !body.coil) {
        toast.error(`${bc}: not found`);
        return;
      }
      const coil = body.coil;
      if (!["sfg", "fg"].includes(coil.kind)) {
        toast.error(`${bc}: not an FG/SFG (kind=${coil.kind})`);
        return;
      }
      if (coil.status !== "received") {
        toast.error(`${bc}: not available (status=${coil.status})`);
        return;
      }
      if (coil.kind === "fg") {
        setPendingFg({
          barcode: bc,
          kind: "fg",
          sku_name: coil.sku_name || "",
          weight_kg: coil.weight_kg || 0,
          grade: coil.grade || "",
          width_mm: coil.width_mm,
          thickness_mm: coil.thickness_mm,
        });
        setPartialMode(false);
        setPartialInput("");
        return;
      }
      await persistScan(bc);
      toast.success(`${bc} added — scan next`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed — retry");
    }
  };

  const confirmDispatchFull = async () => {
    if (!pendingFg) return;
    try {
      await persistScan(pendingFg.barcode);
      toast.success(`${pendingFg.barcode} added — scan next`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed — retry");
      return;
    }
    setPendingFg(null);
    setPartialMode(false);
    setPartialInput("");
  };

  const confirmDispatchPartial = async () => {
    if (!pendingFg) return;
    const partial = Number(partialInput);
    if (!Number.isFinite(partial) || partial <= 0) {
      toast.error("Enter a partial weight greater than 0");
      return;
    }
    if (partial >= pendingFg.weight_kg) {
      toast.error(`Partial must be less than ${pendingFg.weight_kg} kg — use full dispatch`);
      return;
    }
    setSplitPending(true);
    try {
      const res = await splitFGPallet(pendingFg.barcode, partial);
      // Persist the newly-created (dispatched portion) FG-N to the SO's scan list.
      await persistScan(res.dispatched_new.coil_id);
      toast.success(`Split ok · leftover ${res.leftover.coil_id} = ${res.leftover.weight_kg}kg · dispatching ${res.dispatched_new.coil_id} = ${res.dispatched_new.weight_kg}kg`);
      setSplitResult(res);
      setPendingFg(null);
      setPartialMode(false);
      setPartialInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed — retry");
    } finally {
      setSplitPending(false);
    }
  };

  const cancelPendingFg = () => {
    setPendingFg(null);
    setPartialMode(false);
    setPartialInput("");
  };

  const removeScan = async (bc) => {
    try {
      const res = await removeSOScan(orderId, bc);
      setScanned((prev) => prev.filter((s) => s.barcode !== bc));
      if (res.so_status) {
        qc.invalidateQueries({ queryKey: ["order", orderId] });
        qc.invalidateQueries({ queryKey: ["orders"] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove scan");
    }
  };

  // -----------------------------------------------------------------
  // e-Invoice upload → Gemini extract
  // -----------------------------------------------------------------
  const handleInvoiceUpload = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const data = await extractEInvoice(file);
      setEinvoice(data);
      // Auto-populate matching fields (worker can edit)
      if (data.gr_rr_number && !grRr) setGrRr(data.gr_rr_number);
      if (data.station && !station) setStation(data.station);
      if (data.vehicle_number && !vehicle) setVehicle(data.vehicle_number);
      // Grand Total → editable rupee input. Only auto-fill if operator hasn't typed anything yet.
      if (data.grand_total_inr && !invoiceAmount) setInvoiceAmount(String(data.grand_total_inr));
      toast.success(`Invoice ${data.invoice_number || "?"} extracted — ${data.lines?.length ?? 0} line(s)`);
    } catch (e) {
      toast.error(errMsg(e, "Extraction failed — try a clearer PDF"));
    } finally {
      setExtracting(false);
    }
  };

  const handleSlipUpload = async (file) => {
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Slip too large (max ~2 MB)"); return; }
    setExtractingSlip(true);
    try {
      const data = await extractKantaParchi(file);
      setSlip({
        filename: data.filename || file.name,
        b64: data.file_b64,
        mime_type: data.mime_type,
        extracted: data,
      });
      if (data.net_weight_kg && !kantaNet) setKantaNet(String(data.net_weight_kg));
      if (data.vehicle_number && !vehicle) setVehicle(data.vehicle_number);
      toast.success(`Kanta Parchi read — net ${data.net_weight_kg ?? "?"} kg (please verify)`);
    } catch (e) {
      // Fall back to plain attach on OCR failure — worker types weight
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result?.toString() || "";
        const b64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
        setSlip({ filename: file.name, b64, mime_type: file.type, extracted: null });
        toast.warning("Kanta Parchi attached — please type net weight manually");
      };
      reader.readAsDataURL(file);
    } finally {
      setExtractingSlip(false);
    }
  };

  // -----------------------------------------------------------------
  // Derived: per-SKU scanned totals + invoice map + eligibility check
  // -----------------------------------------------------------------
  const scannedByKey = useMemo(() => {
    const m = {};
    for (const s of scanned) {
      const key = `${s.kind}|${(s.sku_name || "").toLowerCase()}`;
      m[key] = (m[key] || 0) + (s.weight_kg || 0);
    }
    return m;
  }, [scanned]);

  const invoiceKgBySku = useMemo(() => {
    const m = {};
    for (const ln of (einvoice?.lines || [])) {
      const k = (ln.sku_name || "").toLowerCase();
      if (!k) continue;
      m[k] = (m[k] || 0) + (ln.weight_kg || 0);
    }
    return m;
  }, [einvoice]);

  const openLines = useMemo(() => {
    if (!order) return [];
    return (order.lines || []).filter((l) => !l.short_closed_at && !l.delivered_at && (l.ordered_kg - (l.delivered_kg || 0)) > 0);
  }, [order]);

  // -----------------------------------------------------------------
  // Three-way weight match (scanned vs invoice net vs Kanta Parchi net)
  // Tolerance ±100 kg on totals — matches backend DISPATCH_TOTAL_WEIGHT_TOLERANCE_KG
  // -----------------------------------------------------------------
  const scannedTotalKg = useMemo(
    () => scanned.reduce((sum, s) => sum + (s.weight_kg || 0), 0),
    [scanned],
  );
  const invoiceNetKg = useMemo(() => {
    if (!einvoice) return null;
    if (einvoice.net_weight_kg != null) return Number(einvoice.net_weight_kg);
    const s = (einvoice.lines || []).reduce((a, ln) => a + (Number(ln.weight_kg) || 0), 0);
    return s || null;
  }, [einvoice]);
  const kantaNetKg = useMemo(() => (kantaNet === "" || kantaNet == null ? null : Number(kantaNet)), [kantaNet]);
  const TOL_KG = 100;
  const diffScannedInvoice = invoiceNetKg == null ? null : scannedTotalKg - invoiceNetKg;
  const diffScannedKanta = kantaNetKg == null ? null : scannedTotalKg - kantaNetKg;
  const diffInvoiceKanta = (invoiceNetKg == null || kantaNetKg == null) ? null : invoiceNetKg - kantaNetKg;
  const weightsMatch = (
    diffScannedInvoice != null && Math.abs(diffScannedInvoice) <= TOL_KG &&
    diffScannedKanta != null && Math.abs(diffScannedKanta) <= TOL_KG &&
    diffInvoiceKanta != null && Math.abs(diffInvoiceKanta) <= TOL_KG
  );

  // Only the three-way TOTAL weight is checked (scanned vs invoice net vs kanta).
  // The system used to compare per-SKU weights against invoice lines — that's
  // been dropped by design because real invoices describe items differently.
  const skuMismatch = false;

  // The save is allowed even when weights don't match — the backend flags the
  // dispatch as pending-management-approval. Structural gates only:
  // scanned coils + valid invoice # + Kanta Parchi + every scanned SKU is on
  // an open line of this order.
  const canSave = useMemo(() => {
    if (scanned.length === 0) return false;
    if (!einvoice || !einvoice.invoice_number) return false;
    if (!slip?.b64) return false;                   // Kanta Parchi mandatory
    if (!(kantaNetKg > 0)) return false;
    const scannedKeys = Object.keys(scannedByKey);
    if (scannedKeys.length === 0) return false;
    for (const key of scannedKeys) {
      const [kind, skuLc] = key.split("|");
      const matched = openLines.some((l) => (l.kind || "sfg") === kind && (l.sku_name || "").toLowerCase() === skuLc);
      if (!matched) return false;
    }
    return true;
  }, [scanned.length, scannedByKey, einvoice, openLines, slip, kantaNetKg]);

  const needsApproval = !weightsMatch || skuMismatch;

  // -----------------------------------------------------------------
  // Save mutation
  // -----------------------------------------------------------------
  const gpWinRef = useRef(null);
  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        barcodes: scanned.map((s) => s.barcode),
        e_invoice_number: (einvoice.invoice_number || "").trim(),
        e_invoice_date: einvoice.invoice_date || "",
        e_invoice_pdf_b64: einvoice.pdf_b64 || "",
        e_invoice_filename: einvoice.filename || "",
        invoice_lines: (einvoice.lines || []).map((ln) => ({ sku_name: ln.sku_name, weight_kg: ln.weight_kg || 0 })),
        invoice_net_weight_kg: invoiceNetKg || 0,
        // invoiceAmount is a controlled string in the UI. Send a real number when
        // present, otherwise omit (backend keeps it null vs. a misleading ₹ 0).
        invoice_amount_inr: (() => {
          const parsed = parseFloat(invoiceAmount);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        })(),
        gr_rr_number: grRr,
        station,
        weight_bridge_slip_b64: slip?.b64 || "",
        weight_bridge_slip_filename: slip?.filename || "",
        kanta_parchi_net_kg: kantaNetKg || 0,
        vehicle_number: vehicle,
        driver_name: driver,
        notes,
      };
      return executeSODispatch(orderId, payload);
    },
    onSuccess: (res) => {
      if (res?.approval_status === "pending") {
        toast.warning("Dispatch saved · waiting for management approval (weight mismatch)");
      } else {
        toast.success("Dispatch closed");
      }
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["dispatch-history"] });
      if (res?.dispatch?.id) {
        openPdfInWindow(dispatchGatePassPdfUrl(res.dispatch.id), gpWinRef.current).catch((e) => toast.error(e.message));
      }
      onBack();
    },
    onError: (e) => {
      if (gpWinRef.current && !gpWinRef.current.closed) gpWinRef.current.close();
      toast.error(errMsg(e, "Failed to close dispatch"));
    },
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!order) return <p className="text-sm text-red-700">Order not found.</p>;

  const scannedTotalKgRounded = Math.round(scannedTotalKg * 1000) / 1000;

  return (
    <div className="space-y-5" data-testid="dispatch-execute">
      {/* header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <button onClick={onBack} data-testid="dispatch-back" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to orders
          </button>
          <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Step 2</p>
          <h2 className="font-heading text-2xl font-bold text-slate-900 font-mono">{order.order_number} · {order.customer_name}</h2>
          <p className="text-xs text-slate-500">
            {order.po_number && <>PO {order.po_number} · </>}
            Target {order.target_delivery_date || "—"}
          </p>
        </div>
      </div>

      {/* Last rejection's action-required note — operator's to-do list before re-dispatch */}
      {lastRejected && (!pendingApprovals.length || !pendingApprovals.some((p) => p.id === lastRejected.id)) && (
        <section
          data-testid="action-required-banner"
          className="border rounded-sm p-3 bg-red-50 border-red-200"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-700" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Last dispatch rejected — action required
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Invoice <b className="font-mono">{lastRejected.e_invoice_number || "—"}</b>
                {lastRejected.rejected_by && <> · by {lastRejected.rejected_by}</>}
                {lastRejected.rejected_at && <> · {new Date(lastRejected.rejected_at).toLocaleString()}</>}
              </p>
              <p className="mt-2 text-sm text-slate-800 bg-white border border-red-200 rounded-sm px-2 py-1.5 whitespace-pre-wrap" data-testid="action-required-note">
                {lastRejected.action_required || "(no note)"}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">Coils are back in stock. Fix the above and re-scan / re-dispatch.</p>
            </div>
          </div>
        </section>
      )}

      {/* Pending-approval banner — visible whenever a dispatch on this SO is
          waiting for director sign-off. Director gets an inline Approve button;
          everyone else just sees the "Pending director approval" chip. */}
      {pendingApprovals.length > 0 && (
        <section
          data-testid="pending-approval-banner"
          className={`border rounded-sm p-3 ${isApprover ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`h-5 w-5 shrink-0 ${isApprover ? "text-red-700" : "text-amber-700"}`} />
            <div className="flex-1 min-w-0 space-y-2">
              <p className={`text-sm font-semibold ${isApprover ? "text-red-800" : "text-amber-800"}`}>
                {isApprover ? "Management approval required" : "Pending management approval"}
              </p>
              {pendingApprovals.map((d) => (
                <div key={d.id} className="bg-white border border-slate-200 rounded-sm p-2.5 space-y-1.5" data-testid={`pending-dispatch-${d.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-slate-700">
                      Invoice <b className="font-mono">{d.e_invoice_number || "—"}</b>
                      {d.vehicle_number && <> · Vehicle <b className="font-mono">{d.vehicle_number}</b></>}
                      {d.dispatch_date && <> · {d.dispatch_date}</>}
                    </div>
                    {isApprover && (
                      <div className="flex gap-1.5 items-center">
                        <Button
                          size="sm"
                          data-testid={`approve-dispatch-${d.id}`}
                          onClick={() => approveMut.mutate(d.id)}
                          disabled={approveMut.isPending || rejectMut.isPending}
                          className="bg-emerald-700 hover:bg-emerald-800 text-white h-8"
                        >
                          {approveMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`reject-dispatch-${d.id}`}
                          onClick={() => { setRejecting(d); setActionNote(""); }}
                          disabled={approveMut.isPending || rejectMut.isPending}
                          className="border-red-300 text-red-700 hover:bg-red-50 h-8"
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                  {(d.mismatches || []).length > 0 && (
                    <ul className="text-[11px] text-slate-600 list-disc pl-4 space-y-0.5">
                      {(d.mismatches || []).map((m, i) => (
                        <li key={i}>{m.message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Requested lines: FG then SFG */}
      <section>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Requested SKUs on this order</p>
        <div className="space-y-2">
          {["fg", "sfg"].map((k) => {
            const grp = openLines.filter((l) => (l.kind || "sfg") === k);
            if (grp.length === 0) return null;
            return (
              <div key={k} className="space-y-2">
                {grp.map((line, i) => {
                  const key = `${k}|${(line.sku_name || "").toLowerCase()}`;
                  const scannedKg = Math.round((scannedByKey[key] || 0) * 100) / 100;
                  const invoiceKg = invoiceKgBySku[(line.sku_name || "").toLowerCase()];
                  return <RequestedLineRow key={`${k}-${i}`} line={line} scannedKg={scannedKg} invoiceKg={invoiceKg} />;
                })}
              </div>
            );
          })}
          {openLines.length === 0 && (
            <p className="text-sm text-slate-500 italic">No open lines on this order.</p>
          )}
        </div>
      </section>

      {/* Scan QR codes */}
      <section className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3" data-testid="dispatch-scan-box">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Scan</p>
            <h3 className="font-heading text-base font-bold text-slate-900">Scan FG or SFG QR codes</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">System auto-detects the kind. Duplicates are ignored.</p>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant={cameraOn ? "default" : "outline"} data-testid="toggle-camera" onClick={() => setCameraOn((v) => !v)} className={cameraOn ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}>
              <Camera className="h-4 w-4 mr-1.5" /> {cameraOn ? "Stop camera" : "Use camera"}
            </Button>
          </div>
        </div>
        {cameraOn && <BarcodeScanner active={cameraOn} onDetected={(v) => addBarcode(v)} />}

        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); addBarcode(manualBc); setManualBc(""); }}
        >
          <input
            ref={manualBcRef}
            data-testid="manual-bc-input"
            value={manualBc}
            onChange={(e) => setManualBc(e.target.value)}
            placeholder="Type or scan a QR code (FG-1, SFG-1, SRE-…) then Enter"
            className="flex-1 h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
            autoFocus
          />
          <Button data-testid="manual-bc-submit" type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">
            <ScanLine className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </form>

        {scanned.length > 0 && (
          <div className="space-y-1" data-testid="scanned-list">
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Scanned ({scanned.length}) · {scannedTotalKgRounded} kg</p>
            <div className="flex flex-wrap gap-1.5">
              {scanned.map((s) => {
                // "Unmatched" = SFG with no sku_name resolved (dimensions
                // missing from SFG SKU master). Operator sees an amber chip
                // so they know to add the SKU under Masters, then re-scan.
                const unmatched = (s.kind === "sfg") && !(s.sku_name || "").trim();
                return (
                  <span
                    key={s.barcode}
                    data-testid={`scanned-pill-${s.barcode}`}
                    data-unmatched={unmatched ? "true" : "false"}
                    title={unmatched ? "No matching SFG SKU — add the master, then re-scan" : undefined}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2 py-1 rounded-sm bg-white border ${
                      unmatched ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-300"
                    }`}
                  >
                    <KindPill kind={s.kind} />
                    {s.barcode} · {fmtCoilKg(s.kind, s.weight_kg)}kg
                    {unmatched && (
                      <span className="text-amber-700" aria-hidden="true" title="No matching SFG SKU on this order">⚠</span>
                    )}
                    <button data-testid={`remove-scan-${s.barcode}`} onClick={() => removeScan(s.barcode)} className="text-slate-400 hover:text-red-600 ml-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* e-Invoice upload + extraction */}
      <section className="bg-white border border-slate-200 rounded-sm p-4" data-testid="dispatch-einvoice-box">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">e-Invoice</p>
            <h3 className="font-heading text-base font-bold text-slate-900">Upload Tax Invoice · auto-extract</h3>
          </div>
          <div>
            <input
              ref={einvoiceInputRef}
              type="file"
              accept="application/pdf"
              data-testid="einvoice-file"
              onChange={(e) => handleInvoiceUpload(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              data-testid="einvoice-pick"
              onClick={() => einvoiceInputRef.current?.click()}
              disabled={extracting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {extracting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {einvoice ? "Replace PDF" : "Upload PDF"}
            </Button>
          </div>
        </div>

        {einvoice ? (
          <div className="mt-3 space-y-2" data-testid="einvoice-preview">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Invoice #</p>
                <p className="font-mono font-bold text-slate-900">{einvoice.invoice_number || "—"}</p>
              </div>
              <div>
                <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Invoice date</p>
                <p className="font-mono font-bold text-slate-900">{einvoice.invoice_date || "—"}</p>
              </div>
              <div>
                <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Customer (extracted)</p>
                <p className="font-mono font-bold text-slate-900 truncate">{einvoice.customer_name || "—"}</p>
              </div>
            </div>
            <div className="text-xs">
              <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold mb-1">Extracted lines</p>
              <ul className="space-y-0.5">
                {(einvoice.lines || []).map((ln, i) => (
                  <li key={`${ln.sku_name}-${i}`} className="flex justify-between font-mono">
                    <span>{ln.sku_name}</span>
                    <span className="font-bold">{ln.weight_kg} kg</span>
                  </li>
                ))}
                {(!einvoice.lines || einvoice.lines.length === 0) && (
                  <li className="text-red-700 italic">No lines extracted — try a clearer PDF.</li>
                )}
              </ul>
            </div>
            {einvoice.customer_name && order.customer_name &&
              !sameCustomer(einvoice.customer_name, order.customer_name) && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Invoice customer ({einvoice.customer_name}) ≠ order customer ({order.customer_name}).
              </p>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-slate-500 italic mt-3 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Upload the Tax Invoice PDF. Gemini extracts customer + per-SKU weights automatically.
          </p>
        )}
      </section>

      {/* Kanta Parchi (Weighbridge slip) — MANDATORY with AI-extracted net weight */}
      <section className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="dispatch-slip-box">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Mandatory</p>
            <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
              Kanta Parchi <span className="text-[10px] font-medium text-slate-500">(weighbridge slip)</span>
              {slip && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            </h3>
            <p className="text-[11px] text-slate-500">Upload photo/PDF — AI extracts net weight; you confirm before dispatch closes.</p>
          </div>
          <div>
            <input
              ref={slipInputRef}
              type="file"
              accept="application/pdf,image/*"
              data-testid="slip-file"
              onChange={(e) => handleSlipUpload(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              data-testid="slip-pick"
              variant="outline"
              onClick={() => slipInputRef.current?.click()}
              disabled={extractingSlip}
            >
              {extractingSlip ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {slip ? "Replace slip" : "Upload Kanta Parchi"}
            </Button>
          </div>
        </div>
        {slip && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="sm:col-span-2 text-xs font-mono text-slate-700 flex items-center gap-1" data-testid="slip-name">
              <FileText className="h-3.5 w-3.5" /> {slip.filename}
              <button onClick={() => { setSlip(null); setKantaNet(""); }} className="ml-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
              {slip.extracted?.slip_number && <span className="ml-2 text-slate-500">Slip #{slip.extracted.slip_number}</span>}
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Confirm net weight (kg)</label>
              <input
                data-testid="kanta-net-input"
                type="number"
                min="0"
                step="0.1"
                value={kantaNet}
                onChange={(e) => setKantaNet(e.target.value)}
                placeholder="0"
                className="mt-1 w-full h-10 px-2 border-2 border-slate-300 rounded-sm text-base font-mono focus:border-slate-900 outline-none"
              />
              {slip.extracted?.net_weight_kg != null && (
                <p className="text-[10px] text-slate-400 mt-1">AI: {slip.extracted.net_weight_kg} kg</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Three-way weight match indicator */}
      {(scanned.length > 0 || einvoice || slip) && (
        <WeightMatchBanner
          scannedKg={scannedTotalKgRounded}
          invoiceKg={invoiceNetKg}
          kantaKg={kantaNetKg}
          tolerance={TOL_KG}
        />
      )}

      {/* Trip meta + save */}
      <section className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Vehicle</label>
            <input data-testid="vehicle-input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="DL01LU4248" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">GR / RR No</label>
            <input data-testid="gr-rr-input" value={grRr} onChange={(e) => setGrRr(e.target.value)} placeholder="17056 / 24-07-2026" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Station</label>
            <input data-testid="station-input" value={station} onChange={(e) => setStation(e.target.value)} placeholder="BADDI (H.P)" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Driver</label>
            <input data-testid="driver-input" value={driver} onChange={(e) => setDriver(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
              Invoice Amount (₹) <span className="normal-case tracking-normal text-slate-400 font-normal">· Grand Total</span>
            </label>
            <input
              data-testid="invoice-amount-input"
              type="number" step="0.01" min="0"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              placeholder="1,43,158.00"
              className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Notes</label>
            <input data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-xs text-slate-600">
            <p><b className="font-mono">{scanned.length}</b> scanned · <b className="font-mono">{scannedTotalKgRounded} kg</b></p>
            {einvoice && <p className="text-[11px] text-slate-500">Invoice net: <b className="font-mono">{invoiceNetKg ?? "—"} kg</b></p>}
          </div>
          <Button
            data-testid="dispatch-close-btn"
            disabled={!canSave || saveMut.isPending}
            onClick={() => { gpWinRef.current = window.open("", "_blank"); saveMut.mutate(); }}
            className={needsApproval ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-700 hover:bg-emerald-800 text-white"}
          >
            {saveMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> :
              needsApproval ? <AlertTriangle className="h-4 w-4 mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            {needsApproval ? "Save · needs management approval" : "Close order & save dispatch"}
          </Button>
        </div>
        {canSave && needsApproval && (
          <p className="text-[11px] text-amber-800 flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="approval-warning">
            <AlertTriangle className="h-3 w-3" /> Weights don't fully agree — the dispatch will be saved
            and the vehicle can leave, but the sales order will stay open until management approves.
          </p>
        )}
        {!canSave && (scanned.length > 0 || einvoice) && (
          <p className="text-[11px] text-amber-800 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />{" "}
            {!slip?.b64 ? "Kanta Parchi upload is mandatory."
             : !(kantaNetKg > 0) ? "Confirm Kanta Parchi net weight."
             : !einvoice ? "Upload the Tax Invoice PDF."
             : "Every scanned SKU must belong to an open line on this order."}
          </p>
        )}
      </section>

      {/* Full/Partial FG choice dialog */}
      <Dialog open={!!pendingFg} onOpenChange={(o) => { if (!o) cancelPendingFg(); }}>
        <DialogContent data-testid="fg-full-partial-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-4 w-4 text-emerald-700" />
              Dispatch <span className="font-mono">{pendingFg?.barcode}</span>
            </DialogTitle>
            <DialogDescription>
              SKU <b className="font-mono">{pendingFg?.sku_name || "—"}</b> · Recorded weight <b className="font-mono">{pendingFg?.weight_kg} kg</b>.
              Choose whether to dispatch the whole pallet, or split it into a partial dispatch plus a leftover pallet that stays in inventory.
            </DialogDescription>
          </DialogHeader>

          {!partialMode ? (
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                data-testid="dispatch-full-btn"
                onClick={confirmDispatchFull}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Dispatch full {pendingFg?.barcode}
              </Button>
              <Button
                data-testid="dispatch-partial-btn"
                variant="outline"
                onClick={() => { setPartialMode(true); setPartialInput(""); }}
                className="flex-1"
              >
                <Scissors className="h-4 w-4 mr-1.5" /> Dispatch partial…
              </Button>
            </div>
          ) : (
            <div className="space-y-2 pt-1">
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Partial qty to dispatch (kg)</label>
              <input
                autoFocus
                data-testid="partial-weight-input"
                type="number"
                min="0"
                step="0.001"
                max={pendingFg?.weight_kg}
                value={partialInput}
                onChange={(e) => setPartialInput(e.target.value)}
                placeholder={`< ${pendingFg?.weight_kg}`}
                className="w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
              />
              <p className="text-[11px] text-slate-500">
                Leftover pallet <b className="font-mono">{pendingFg?.barcode}</b> will keep its ID; a new FG-ID is generated for the dispatched portion.
              </p>
              <DialogFooter className="pt-2 flex-col sm:flex-row gap-2">
                <Button
                  data-testid="partial-back-btn"
                  variant="outline"
                  onClick={() => setPartialMode(false)}
                  disabled={splitPending}
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
                <Button
                  data-testid="partial-confirm-btn"
                  onClick={confirmDispatchPartial}
                  disabled={splitPending}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {splitPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Scissors className="h-4 w-4 mr-1.5" />}
                  Split & add to dispatch
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Split result — print two stickers */}
      <Dialog open={!!splitResult} onOpenChange={(o) => { if (!o) { setSplitResult(null); manualBcRef.current?.focus(); } }}>
        <DialogContent data-testid="split-result-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-4 w-4 text-emerald-700" /> Split complete — print new stickers
            </DialogTitle>
            <DialogDescription>
              The old sticker on the physical pallet is now stale — print both labels and stick them on the correct pile before loading the truck.
            </DialogDescription>
          </DialogHeader>

          {splitResult && (
            <div className="space-y-2 pt-1 text-sm">
              <div className="flex items-center justify-between gap-2 p-2 border border-slate-200 rounded-sm bg-slate-50">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Leftover — stays in inventory</p>
                  <p className="font-mono font-bold text-slate-900">{splitResult.leftover.coil_id} · {splitResult.leftover.weight_kg} kg</p>
                </div>
                <PrintButton
                  testId="print-leftover-btn"
                  tspl={fgLabelsTsplUrl([splitResult.leftover.coil_id])}
                  pdf={fgLabelsPdfUrl([splitResult.leftover.coil_id])}
                  label={`leftover ${splitResult.leftover.coil_id}`}
                >
                  Print leftover
                </PrintButton>
              </div>
              <div className="flex items-center justify-between gap-2 p-2 border border-emerald-200 rounded-sm bg-emerald-50">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-emerald-800 font-semibold">Dispatched portion — new FG-ID</p>
                  <p className="font-mono font-bold text-slate-900">{splitResult.dispatched_new.coil_id} · {splitResult.dispatched_new.weight_kg} kg</p>
                </div>
                <PrintButton
                  testId="print-dispatched-btn"
                  tspl={fgLabelsTsplUrl([splitResult.dispatched_new.coil_id])}
                  pdf={fgLabelsPdfUrl([splitResult.dispatched_new.coil_id])}
                  label={`dispatched ${splitResult.dispatched_new.coil_id}`}
                  variant="primary"
                >
                  Print dispatched
                </PrintButton>
              </div>
              <DialogFooter className="pt-2">
                <Button
                  data-testid="split-result-done-btn"
                  onClick={() => { setSplitResult(null); manualBcRef.current?.focus(); }}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject-dispatch modal — captures the required "Action Required" note */}
      <Dialog open={!!rejecting} onOpenChange={(v) => { if (!v) { setRejecting(null); setActionNote(""); } }}>
        <DialogContent data-testid="reject-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject dispatch & put coils back in stock</DialogTitle>
            <DialogDescription>
              This will reverse dispatch <b className="font-mono">{rejecting?.e_invoice_number || "—"}</b>{" "}
              — {(rejecting?.scanned_items?.length || rejecting?.lines_updated?.length || 0)} coil(s) will return to `received` status
              and the SO's delivered quantity will be rolled back.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] tracking-widest uppercase font-semibold text-slate-600">
              Action Required <span className="text-red-600">*</span>
            </label>
            <textarea
              data-testid="action-required-input"
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              placeholder="e.g. Re-weigh at gate, correct invoice net weight to 3,240 kg, re-scan coils and re-submit."
              rows={4}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm resize-none"
            />
            <p className="text-[11px] text-slate-500">The operator will see this note on the SO tile so they know exactly what to fix before re-dispatching.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setActionNote(""); }}>Cancel</Button>
            <Button
              data-testid="reject-confirm-btn"
              onClick={() => rejectMut.mutate({ id: rejecting.id, note: actionNote.trim() })}
              disabled={!actionNote.trim() || rejectMut.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {rejectMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />}
              Reject & reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// -------------------------------------------------------------------
// Root
// -------------------------------------------------------------------
const Dispatch = () => {
  const [orderId, setOrderId] = useState(null);
  const [tab, setTab] = useState("live"); // live | history
  const { hasModule } = useAuth();
  const canHistory = hasModule("dispatch.history");
  useEffect(() => { if (typeof window !== "undefined") window.scrollTo({ top: 0 }); }, [orderId, tab]);
  return (
    <div data-testid="dispatch-page" className="max-w-6xl mx-auto space-y-6">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Truck className="h-7 w-7 text-slate-900" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Dispatch &amp; Logistics</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Dispatch</h1>
          </div>
        </div>
        {canHistory && !orderId && (
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden text-sm" data-testid="dispatch-tabs">
            <button
              onClick={() => setTab("live")}
              data-testid="dispatch-tab-live"
              className={`px-3 py-1.5 ${tab === "live" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            >
              <Truck className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Live
            </button>
            <button
              onClick={() => setTab("history")}
              data-testid="dispatch-tab-history"
              className={`px-3 py-1.5 border-l border-slate-200 ${tab === "history" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            >
              <History className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Dispatch History
            </button>
          </div>
        )}
      </div>
      {orderId
        ? <DispatchExecute orderId={orderId} onBack={() => setOrderId(null)} />
        : (tab === "history" && canHistory
            ? <DispatchHistoryTab />
            : <PendingTiles onPick={setOrderId} />)
      }
    </div>
  );
};

// -------------------------------------------------------------------
// Dispatch History — table of all committed dispatches with drill-down
// + multi-customer / date-range filters + amount sort + xlsx/pdf export
// -------------------------------------------------------------------

// Compute date_from for a "last X" preset (returns YYYY-MM-DD or "")
const presetFrom = (preset) => {
  const d = new Date();
  switch (preset) {
    case "1m": d.setMonth(d.getMonth() - 1); break;
    case "3m": d.setMonth(d.getMonth() - 3); break;
    case "6m": d.setMonth(d.getMonth() - 6); break;
    case "1y": d.setFullYear(d.getFullYear() - 1); break;
    default: return "";
  }
  return d.toISOString().slice(0, 10);
};

const fmtINR = (v) => {
  const n = Number(v || 0);
  if (!n) return "—";
  return `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DispatchHistoryTab = () => {
  const [detailFor, setDetailFor] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [datePreset, setDatePreset] = useState("all");     // all | 1m | 3m | 6m | 1y | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const custDropdownRef = useRef(null);
  useEffect(() => {
    if (!showCustDropdown) return;
    const onDocClick = (e) => {
      if (custDropdownRef.current && !custDropdownRef.current.contains(e.target)) {
        setShowCustDropdown(false);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setShowCustDropdown(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showCustDropdown]);

  const dateFrom = datePreset === "custom" ? customFrom : presetFrom(datePreset);
  const dateTo   = datePreset === "custom" ? customTo   : "";

  const listParams = useMemo(() => {
    const p = { sort };
    if (selectedCustomers.size) p.customers = Array.from(selectedCustomers).join(",");
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    return p;
  }, [selectedCustomers, dateFrom, dateTo, sort]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["dispatch-history", listParams],
    queryFn: () => listDispatchHistory(listParams),
    refetchInterval: 30_000,
  });
  const { data: allCustomers = [] } = useQuery({
    queryKey: ["dispatch-history-customers"],
    queryFn: listDispatchHistoryCustomers,
    staleTime: 60_000,
  });

  const totalAmount = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.invoice_amount_inr || 0), 0),
    [rows],
  );

  const toggleCustomer = (name) => {
    setSelectedCustomers((prev) => {
      const nx = new Set(prev);
      if (nx.has(name)) nx.delete(name); else nx.add(name);
      return nx;
    });
  };
  const clearFilters = () => {
    setSelectedCustomers(new Set());
    setDatePreset("all");
    setCustomFrom("");
    setCustomTo("");
    setSort("date_desc");
  };

  return (
    <div className="space-y-4" data-testid="dispatch-history">
      {/* ---------------- Filter / Sort / Export bar ---------------- */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          {/* Multi-customer dropdown */}
          <div className="relative" ref={custDropdownRef}>
            <label className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold block mb-1">Customer</label>
            <button
              type="button"
              data-testid="cust-filter-toggle"
              onClick={() => setShowCustDropdown((v) => !v)}
              className="h-9 min-w-[220px] px-2 border border-slate-300 rounded-sm text-sm bg-white text-left inline-flex items-center justify-between gap-2 hover:border-slate-500"
            >
              <span className="truncate">
                {selectedCustomers.size === 0
                  ? "All customers"
                  : selectedCustomers.size === 1
                    ? Array.from(selectedCustomers)[0]
                    : `${selectedCustomers.size} selected`}
              </span>
              <ArrowLeft className={`h-3 w-3 rotate-[-90deg] transition-transform ${showCustDropdown ? "rotate-90" : ""}`} />
            </button>
            {showCustDropdown && (
              <div
                className="absolute z-20 mt-1 w-[260px] max-h-72 overflow-y-auto bg-white border border-slate-300 rounded-sm shadow-lg"
                data-testid="cust-filter-dropdown"
              >
                {allCustomers.length === 0 ? (
                  <p className="p-3 text-xs text-slate-500">No customers yet</p>
                ) : allCustomers.map((c) => (
                  <label key={c} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      data-testid={`cust-opt-${c}`}
                      checked={selectedCustomers.has(c)}
                      onChange={() => toggleCustomer(c)}
                      className="h-4 w-4"
                    />
                    <span className="truncate">{c}</span>
                  </label>
                ))}
                {selectedCustomers.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCustomers(new Set())}
                    className="w-full text-left px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 border-t border-slate-200"
                  >
                    Clear customers
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Date preset */}
          <div>
            <label className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold block mb-1">Dispatch Period</label>
            <select
              data-testid="date-preset"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white"
            >
              <option value="all">All time</option>
              <option value="1m">Last month</option>
              <option value="3m">Last quarter</option>
              <option value="6m">Last 6 months</option>
              <option value="1y">Last year</option>
              <option value="custom">Custom range…</option>
            </select>
          </div>

          {datePreset === "custom" && (
            <>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold block mb-1">From</label>
                <input type="date" data-testid="date-from" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white font-mono" />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold block mb-1">To</label>
                <input type="date" data-testid="date-to" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white font-mono" />
              </div>
            </>
          )}

          {/* Sort */}
          <div>
            <label className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold block mb-1">Sort by</label>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white"
            >
              <option value="date_desc">Dispatch date · latest first</option>
              <option value="date_asc">Dispatch date · oldest first</option>
              <option value="amount_desc">Invoice amount · high to low</option>
              <option value="amount_asc">Invoice amount · low to high</option>
            </select>
          </div>

          {/* Export + reset */}
          <div className="flex items-center gap-1.5 ml-auto">
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="filter-reset" className="h-9">Reset</Button>
            <a
              href={dispatchHistoryExportUrl("xlsx", listParams)}
              data-testid="export-xlsx"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-slate-300 hover:border-slate-900 text-sm text-slate-700 hover:bg-slate-900 hover:text-white"
              target="_blank" rel="noreferrer"
            >
              <Download className="h-3.5 w-3.5" /> Excel
            </a>
            <a
              href={dispatchHistoryExportUrl("pdf", listParams)}
              data-testid="export-pdf"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-slate-300 hover:border-slate-900 text-sm text-slate-700 hover:bg-slate-900 hover:text-white"
              target="_blank" rel="noreferrer"
            >
              <FileText className="h-3.5 w-3.5" /> PDF
            </a>
          </div>
        </div>
      </div>

      {/* ---------------- Results table ---------------- */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center justify-between">
          <span>Dispatched Sales Orders</span>
          <span className="font-mono text-slate-700">
            <span data-testid="history-count">{rows.length}</span> rows
            {totalAmount > 0 && <span className="ml-3">· Total <b data-testid="history-total-amount">{fmtINR(totalAmount)}</b></span>}
          </span>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No dispatches match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] tracking-widest uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Dispatch Date</th>
                  <th className="text-left px-3 py-2">Invoice #</th>
                  <th className="text-right px-3 py-2">Invoice Amount</th>
                  <th className="text-right px-3 py-2">Total Qty (kg)</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">e-Invoice</th>
                  <th className="text-left px-3 py-2">Kanta Parchi</th>
                  <th className="text-left px-3 py-2">Gate Pass</th>
                  <th className="text-right px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`dispatch-row-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-700">{r.dispatch_date || (r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 font-mono text-slate-800">{r.e_invoice_number || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-900" data-testid={`amount-${r.id}`}>{fmtINR(r.invoice_amount_inr)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{r.scanned_total_kg ?? r.invoice_net_weight_kg ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-700">{r.vehicle_number || "—"}</td>
                    <td className="px-3 py-2">
                      <a href={dispatchEInvoiceUrl(r.id)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`einvoice-dl-${r.id}`}>
                        <Download className="h-3 w-3" /> PDF
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <a href={dispatchKantaParchiUrl(r.id)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`kanta-dl-${r.id}`}>
                        <Download className="h-3 w-3" /> Slip
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      {r.order_id ? (
                        <button
                          type="button"
                          onClick={() => openGatePass(dispatchGatePassPdfUrl(r.id))}
                          className="text-blue-700 hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                          data-testid={`gate-pass-dl-${r.id}`}
                          title="Open the Gate Pass (Dispatch + Guard copy) for this dispatch"
                        >
                          <FileText className="h-3 w-3" /> Gate Pass
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" data-testid={`details-btn-${r.id}`} onClick={() => setDetailFor(r)}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <DispatchDetailsDialog row={detailFor} onClose={() => setDetailFor(null)} />
    </div>
  );
};

const DispatchDetailsDialog = ({ row, onClose }) => {
  const { data: full } = useQuery({
    queryKey: ["dispatch-detail", row?.id],
    queryFn: () => getDispatchDetails(row.id),
    enabled: !!row,
  });
  if (!row) return null;
  const d = full || row;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-details-dialog">
        <DialogHeader>
          <DialogTitle>Dispatch Details — {d.order_number || d.order_id}</DialogTitle>
          <DialogDescription>{d.customer_name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Detail k="Dispatch Date" v={d.dispatch_date || (d.created_at || "").slice(0, 10)} />
          <Detail k="Invoice #" v={d.e_invoice_number} />
          <Detail k="Invoice Date" v={d.e_invoice_date} />
          <Detail k="GR / RR No" v={d.gr_rr_number} />
          <Detail k="Station" v={d.station} />
          <Detail k="Vehicle" v={d.vehicle_number} />
          <Detail k="Driver" v={d.driver_name} />
          <Detail k="Invoice Net" v={d.invoice_net_weight_kg ? `${d.invoice_net_weight_kg} kg` : "—"} />
          <Detail k="Kanta Parchi Net" v={d.kanta_parchi_net_kg ? `${d.kanta_parchi_net_kg} kg` : "—"} />
          <Detail k="Scanned Total" v={d.scanned_total_kg ? `${d.scanned_total_kg} kg` : "—"} />
          <Detail k="Committed By" v={d.created_by} />
          <Detail k="PO #" v={d.po_number || "—"} />
        </div>
        {d.notes && <div className="text-xs text-slate-600 border-t border-slate-100 pt-3">📝 {d.notes}</div>}
        {(d.scanned_items || []).length > 0 && (
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mt-3 mb-1">SKUs shipped</p>
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5">SR ID</th>
                    <th className="text-left px-3 py-1.5">SKU</th>
                    <th className="text-left px-3 py-1.5">Kind</th>
                    <th className="text-right px-3 py-1.5">Weight (kg)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(d.scanned_items || []).map((s) => (
                    <tr key={s.coil_id || s.barcode}>
                      <td className="px-3 py-1.5 font-mono">{s.coil_id || s.barcode}</td>
                      <td className="px-3 py-1.5 font-mono">{s.sku_name}</td>
                      <td className="px-3 py-1.5 uppercase">{s.kind}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{fmtCoilKg(s.kind, s.weight_kg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Detail = ({ k, v }) => (
  <div>
    <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">{k}</p>
    <p className="font-mono font-bold text-slate-900 break-words">{v || "—"}</p>
  </div>
);

export default Dispatch;
